import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";
import { StatusBar, PriorityChip } from "../components/StatusBar";
import { OverdueBadge } from "../components/ItemDetailSheet";
import { ArrowRight, Stack, Package, ClockCounterClockwise, Lightning, Bell, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { logDev } from "../lib/log";
import CriticalPathBanner from "../components/CriticalPathBanner";
import AdminBusinessDashboard from "../components/AdminBusinessDashboard";

export default function DashboardPage() {
  const { user } = useAuth();
  // Super-admin sees the business dashboard, not the procurement workspace.
  if (user?.is_super_admin) {
    return (
      <AppShell>
        <AdminBusinessDashboard />
      </AppShell>
    );
  }
  return <ProcurementDashboard />;
}

function ProcurementDashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [stats, setStats] = useState({});
  const [digest, setDigest] = useState(null);
  const [digestDismissed, setDigestDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  const projectMap = projects.reduce((m, p) => ({ ...m, [p.id]: p }), {});

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const [projsRes, statsRes, digestRes] = await Promise.all([
          api.get("/projects"),
          api.get("/projects/stats").catch(() => ({ data: {} })),
          api.get("/projects/critical-path").catch(() => ({ data: null })),
        ]);
        if (!active) return;
        setProjects(projsRes.data);
        setStats(statsRes.data);
        setDigest(digestRes.data);
        // Pull items across the first few projects
        const itemsByProj = await Promise.all(
          projsRes.data.slice(0, 8).map((p) =>
            api.get(`/projects/${p.id}/items`).then((r) => r.data).catch(() => [])
          )
        );
        const allItems = itemsByProj.flat();
        if (!active) return;
        setItems(allItems);
        if (user?.role !== "contractor") {
          try {
            const { data: rem } = await api.get("/reminders/pending");
            if (active) setReminders(rem);
          } catch (e) {
            logDev("dashboard.reminders", e);
            // non-fatal — reminders are optional
          }
        }
      } catch (e) {
        logDev("dashboard.load", e);
        // non-fatal — dashboard shows fallback empty state
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [user]);

  const kpis = {
    projects: projects.length,
    items: items.length,
    inFlight: items.filter((i) => ["ordered", "shipped", "in_transit"].includes(i.status)).length,
    urgent: items.filter((i) => i.priority === "urgent").length,
  };

  const recent = [...items]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8);

  const markSent = async (id) => {
    try {
      await api.post(`/reminders/${id}/mark-sent`);
      setReminders((rs) => rs.filter((r) => r.id !== id));
      toast.success("Reminder marked sent");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <AppShell>
      <div className="space-y-10" data-testid="dashboard">
        <header className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="label-eyebrow text-[#007BFF]">Overview · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#001E44] mt-2">
              Hello, {user?.name?.split(" ")[0] || "Builder"}.
            </h1>
            <p className="text-zinc-500 mt-2 text-sm sm:text-base">
              Here&apos;s what&apos;s moving across your construction sites today.
            </p>
          </div>
          {user?.role !== "contractor" && (
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 bg-[#007BFF] hover:bg-[#0062cc] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all shadow-sm hover:shadow-md"
              data-testid="dashboard-new-project-link"
            >
              Manage projects <ArrowRight size={14} weight="bold" />
            </Link>
          )}
        </header>

        {/* Critical Path digest — PMs/contractors with 2+ projects only.
            Super-admins see business analytics on a separate dashboard;
            single-project users see the critical path inside their project. */}
        {!digestDismissed && digest && digest.overdue_items > 0 && projects.length >= 2 && (
          <CriticalPathBanner
            digest={digest}
            onDismiss={() => setDigestDismissed(true)}
          />
        )}

        {/* Stat tiles */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile icon={<Stack size={20} weight="bold" />} label="Active Projects" value={kpis.projects} accent="primary" testid="stat-projects" />
          <StatTile icon={<Package size={20} weight="bold" />} label="Total Items" value={kpis.items} accent="zinc" testid="stat-items" />
          <StatTile icon={<ClockCounterClockwise size={20} weight="bold" />} label="In Flight" value={kpis.inFlight} accent="amber" testid="stat-in-flight" />
          <StatTile icon={<Lightning size={20} weight="bold" />} label="Urgent" value={kpis.urgent} accent="accent" testid="stat-urgent" />
        </section>

        {/* Pending reminders */}
        {user?.role !== "contractor" && reminders.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-end justify-between">
              <div>
                <span className="label-eyebrow text-[#007BFF]">Action needed · weekly</span>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-[#001E44] mt-1">
                  Pending order reminders
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  These items were approved but haven&apos;t been ordered yet. Each pending reminder
                  represents a weekly nudge that would be emailed to the contractor + project manager.
                </p>
              </div>
            </div>
            <div className="ctrl-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="ctrl-table min-w-[800px]" data-testid="reminders-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Project</th>
                    <th>Contractor</th>
                    <th>Recipients</th>
                    <th>Approved</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {reminders.map((r) => (
                    <tr key={r.id} data-testid={`reminder-${r.id}`}>
                      <td className="font-semibold">{r.item_name}</td>
                      <td>
                        <Link
                          to={`/projects/${r.project_id}`}
                          className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--primary))] hover:underline"
                        >
                          {r.project_name}
                        </Link>
                      </td>
                      <td className="text-xs">{r.contractor_name}</td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {r.recipients.map((rec) => (
                            <span
                              key={`${r.id}-${rec.role}-${rec.email}`}
                              className="chip bg-zinc-100 text-zinc-700"
                              title={rec.email}
                              data-testid={`reminder-recipient-${r.id}-${rec.role}`}
                            >
                              <Bell size={10} weight="bold" /> {rec.email}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="font-mono text-xs text-muted-foreground">
                        {r.approved_at ? new Date(r.approved_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="text-right">
                        <button
                          onClick={() => markSent(r.id)}
                          className="text-xs font-semibold uppercase tracking-wider text-emerald-700 hover:underline inline-flex items-center gap-1"
                          data-testid={`mark-reminder-sent-${r.id}`}
                        >
                          <CheckCircle size={12} weight="bold" /> Mark sent
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <div className="px-4 py-2.5 bg-zinc-50 border-t border-border text-[11px] text-muted-foreground italic">
                Tip: hook up an email provider later to send these automatically. Reminders re-generate weekly until the item moves to <span className="font-mono">Ordered</span>.
              </div>
            </div>
          </section>
        )}

        {/* Recent activity */}
        <section className="space-y-4">
          <div className="flex items-end justify-between">
            <div>
              <span className="label-eyebrow text-[#007BFF]">Live feed</span>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-[#001E44] mt-1">Recent procurement items</h2>
            </div>
          </div>
          {loading ? (
            <SkeletonRows />
          ) : recent.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="ctrl-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="ctrl-table min-w-[820px]" data-testid="recent-items-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Project</th>
                    <th>Priority</th>
                    <th className="w-[300px]">Status</th>
                    <th>ETA</th>
                    <th>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((i) => (
                    <tr key={i.id} data-testid={`recent-item-${i.id}`}>
                      <td>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{i.name}</span>
                          <OverdueBadge item={i} />
                        </div>
                        <div className="text-xs text-muted-foreground">{i.description?.slice(0, 60)}</div>
                      </td>
                      <td>
                        <Link
                          to={`/projects/${i.project_id}`}
                          className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--primary))] hover:underline"
                          data-testid={`recent-item-project-${i.id}`}
                        >
                          {projectMap[i.project_id]?.name || "—"}
                        </Link>
                      </td>
                      <td><PriorityChip priority={i.priority} /></td>
                      <td><StatusBar status={i.status} compact /></td>
                      <td className="font-mono text-xs">
                        {i.eta ? new Date(i.eta).toLocaleDateString() : "—"}
                      </td>
                      <td className="font-mono text-xs">{i.quantity} {i.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </section>

        {/* Projects grid */}
        {projects.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-end justify-between">
              <div>
                <span className="label-eyebrow text-[#007BFF]">Sites</span>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-[#001E44] mt-1">Your projects</h2>
              </div>
              <Link to="/projects" className="text-sm font-semibold text-[#007BFF] hover:underline" data-testid="link-all-projects">
                See all →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.slice(0, 6).map((p) => {
                const isOverdue = (stats[p.id]?.overdue_count || 0) > 0;
                return (
                  <Link
                    key={p.id}
                    to={`/projects/${p.id}`}
                    className={`ctrl-card overflow-hidden block ${isOverdue ? "glow-overdue" : ""}`}
                    data-testid={`project-card-${p.id}`}
                    aria-label={isOverdue ? `${p.name} — has overdue items` : p.name}
                  >
                    <div className="h-32 bg-zinc-100 overflow-hidden relative">
                      {p.thumbnail_url ? (
                        <img
                          src={p.thumbnail_url}
                          alt={p.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img
                          src="https://images.unsplash.com/photo-1599707254554-027aeb4deacd?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                          alt={p.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {isOverdue && (
                        <span
                          className="absolute top-3 right-3 chip bg-red-600 text-white shadow-md"
                          data-testid={`project-overdue-badge-${p.id}`}
                        >
                          {stats[p.id].overdue_count} overdue
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="font-display font-semibold text-base text-[#001E44] leading-tight">{p.name}</div>
                      <div className="text-xs text-zinc-500 mt-1.5">{p.location || "—"}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}

function StatTile({ icon, label, value, accent, testid }) {
  const accentBg = {
    primary: "bg-[#007BFF]/10 text-[#007BFF]",
    accent: "bg-[#007BFF]/10 text-[#007BFF]",
    amber: "bg-amber-50 text-amber-600",
    zinc: "bg-zinc-100 text-zinc-600",
  }[accent];
  return (
    <div className="ctrl-card p-6" data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <span className="text-xs font-medium text-zinc-500">{label}</span>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accentBg}`}>
          {icon}
        </div>
      </div>
      <div className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#001E44]">{value}</div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="ctrl-card p-6 space-y-3">
      {[...Array(4)].map((_, i) => (
        <div key={`dash-skel-${i}`} className="h-10 bg-zinc-100 rounded animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="ctrl-card p-12 text-center">
      <div className="label-eyebrow mb-2 text-[#007BFF]">Nothing here yet</div>
      <p className="font-display text-xl font-semibold tracking-tight text-[#001E44]">
        Create your first project to start tracking procurement.
      </p>
      <Link
        to="/projects"
        className="inline-flex items-center gap-2 mt-6 bg-[#007BFF] hover:bg-[#0062cc] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all shadow-sm hover:shadow-md"
        data-testid="empty-state-create-project"
      >
        Go to projects <ArrowRight size={14} weight="bold" />
      </Link>
    </div>
  );
}


